import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import {
  fetchCommuncation,
  fetchCommuncationPdf,
} from "../../../../api/customer/communcations";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { PMNotice } from "../../../../components/utils/notifications";
import { DocumentCenter } from "../../../../components/dashboard/documents/document-center";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import {
  HtmlTextFormatter,
  MODULE_URL,
  NewLineFormatter,
} from "../../../../components/Utils";
import { TinyDocumentSection } from "../../../../components/dashboard/documents/tinyDocumentSection";

export const ShowCommuncation = () => {
  const { customer_id, id } = useParams();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const isAdmin = useUserHasAccess({ role: "communcation", action: "approve" });

  const [communcation, setCommuncation] = useState(null);

  useEffect(() => {
    // if ID is set, fetch the communcation
    setTimeout(() => {
      if (id) {
        setIsLoading(true);
        getCommuncation(id);
      } else {
        setIsLoading(false);
      }
    }, 500);
  }, [id]);

  const getCommuncation = (id) => {
    setIsLoading(true);
    try {
      fetchCommuncation({
        id: id,
        customer_id: customer_id,
      }).then((res) => {
        setIsLoading(false);
        setCommuncation(res?.data);
      });
    } catch {
      toast.error("Something went wrong when trying to load the report");
      setIsLoading(false);
      setError(true);
    }
  };

  // get userType form searchParams
  const searchParams = new URLSearchParams(location.search);

  const [errors, setErrors] = useState([]);

  const handleExportPdf = async () => {
    if (!id || !customer_id || exportingPdf) return;
    setExportingPdf(true);
    try {
      const res = await fetchCommuncationPdf({ id, customer_id });
      if (res?.pdf_url) {
        window.open(res.pdf_url, "_blank", "noopener");
      } else {
        toast.error(t("customer.communication.pdf_failed"));
      }
    } catch {
      toast.error(t("customer.communication.pdf_failed"));
    } finally {
      setExportingPdf(false);
    }
  };

  const headerActions = [
    {
      component: null,
      label: exportingPdf
        ? t("customer.communication.pdf_loading")
        : t("customer.communication.export_pdf"),
      description: "",
      path: null,
      action: handleExportPdf,
      icon: DocumentArrowDownIcon,
      textColor: "text-blue-700",
      bgColor: "bg-white",
      hidden: !communcation?.id,
      menu: false,
    },
  ];

  const headerMeta = [];

  function Content() {
    return (
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
            {errors &&
              errors.length > 0 &&
              errors.map((error, index) => (
                <PMNotice
                  severity={"error"}
                  key={index}
                  title="Error!"
                  description={error}
                />
              ))}

            {communcation && (
              <div className="shadow sm:overflow-hidden sm:rounded-md">
                {communcation.metadata?.service_appointment_reminder_id && <p className="bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-700">{t("serviceReminders.history")} · {t(`serviceReminders.state.${communcation.metadata.state}`)}</p>}
                <div>
                  {communcation.related?.id && communcation.related_type && (
                    <div className="flex mb-5">
                      <a
                        href={MODULE_URL(
                          communcation.related_type,
                          communcation.related.id
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-white bg-blue-400 hover:bg-gray-50 hover:text-black"
                      >
                        Gå til {communcation.related_type}
                      </a>
                    </div>
                  )}
                </div>
                <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Sendt til
                      </span>
                      <p className="text-md text-gray-900">
                        {communcation.metadata?.to ||
                          communcation.metadata?.to_number ||
                          "Airon AS"}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Sendt fra
                      </span>
                      <p className="text-md text-gray-900">
                        {communcation.metadata?.from ||
                          communcation.metadata?.from_number ||
                          (communcation.metadata?.service_appointment_reminder_id ? "—" : "Promang (Sveve)")}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Kanal
                      </span>
                      <p className="text-md text-gray-900 capitalize">
                        {communcation.channel}
                      </p>
                    </div>

                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Retning
                      </span>
                      <p className="text-md text-gray-900 capitalize">
                        {communcation.direction}
                      </p>
                    </div>

                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Sendt
                      </span>
                      <p className="text-md text-gray-900">
                        {communcation.sent_at || (communcation.metadata?.service_appointment_reminder_id ? t(`serviceReminders.state.${communcation.metadata.state}`) : "Ukjent")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6 bg-white py-6 px-4 sm:p-6 mt-5">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Innhold
                      </span>
                      <div className="prose max-w-full">
                        {communcation.body_html ? (
                          <HtmlTextFormatter string={communcation.body_html} />
                        ) : (
                          <NewLineFormatter
                            value={communcation.body_text}
                            className="text-md text-gray-900"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {communcation?.id && (
              <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md mb-5">
                <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                  <TinyDocumentSection
                    title="Dokumenter"
                    subtitle="Viser kun dokumenter knyttet til kommunikasjon"
                    module="customer_communication"
                    allowedFileTypes="application/pdf"
                    moduleParam={communcation.id}
                    canUpload={false}
                  />

                  
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <DashboardHeader
        subtitle={
          "Viser kommunikasjon knyttet til " + communcation?.customer?.name
        }
        meta={headerMeta}
        actions={headerActions}
        title={communcation?.subject}
      >
        {isLoading ? <ResourceLoading /> : <Content />}
      </DashboardHeader>
    </>
  );
};
